import useUserCartViews from 'hooks/UserCart/useUserCartViews'
import { memo, useCallback, useMemo, useRef, useState, useEffect } from 'react'
import { matchPath } from 'react-router-dom'
import NavBarButton from '../NavBarButton/NavBarButton'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useToggle from 'hooks/useToggle'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import debounce from 'lodash/debounce'
import {
  isCartAutoPreviewDropdownSuppressed,
  suppressCartAutoDropdown,
} from 'components/Pages/UserCartPage/UserCartPreview/cartAutoDropdownCookie'
import config from 'constants/config'
import { dateDifference } from 'lib/datetime/dateUtils'
import { USER_CART_PREVIEW_MAX_DAYS_TO_SHOW } from 'constants/userCart'
import LineShoppingCartIcon from 'LuxKit/Icons/line/LineShoppingCartIcon'
import Badge from 'LuxKit/Badge'
import { MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import { isDisplayableMultiCartItem } from 'components/Pages/UserCartPage/SharedCarts/multiCartUtils'
import loadable from '@loadable/component'
import { ReportError } from 'actions/ErrorActions'

// Cart review UI is only needed when the dropdown opens. Keep the button/badge
// eager; load the heavy preview as its own chunk, and only on hover/auto-open
// (ssr: false so SSR chunk extraction cannot pull it into the initial page).
const MultiCartDropdownPreview = loadable(
  () => import('components/Pages/UserCartPage/SharedCarts/MultiCartDropdownPreview'),
  { ssr: false },
)

interface Props {
  className?: string
  allowCartPreview?: boolean
}

function NavBarUserCartButton(props: Props) {
  const {
    allowCartPreview = true,
  } = props

  const dispatch = useAppDispatch()
  const multiCartActorRef = MultiCartContext.useActorRef()
  const { items: viewItems } = useUserCartViews(multiCartActorRef, true)
  const numberOfActiveItems = useMemo(() => {
    return viewItems
      .filter(isDisplayableMultiCartItem)
      .filter(item => item.state === 'active' && item.status === 'available')
      .length
  }, [viewItems])

  const userCartUpdatedAt = useMemo(() => {
    const cartItems = viewItems.filter(isDisplayableMultiCartItem)
    if (cartItems.length === 0) {
      return undefined
    }
    // Get the most recent (maximum) lastModifiedAt date
    const dates = cartItems.map(item => new Date(item.lastModifiedAt).getTime())
    return new Date(Math.max(...dates))
  }, [viewItems])

  const currentPath = useAppSelector(state => state.router.location.pathname)
  const isCartPage = useMemo(() => !!matchPath({ path: '/:regionCode/cart', end: true }, currentPath), [currentPath])
  const isAgentHub = config.agentHub.isEnabled
  const isMobile = useIsMobileDevice()
  const wasUpdatedRecently = !userCartUpdatedAt || dateDifference(new Date(), userCartUpdatedAt).days < USER_CART_PREVIEW_MAX_DAYS_TO_SHOW
  const canAutoOpen = numberOfActiveItems > 0 && !isAgentHub && wasUpdatedRecently && !isMobile

  const buttonRef = useRef<HTMLButtonElement>(null)
  const hoverIntentRef = useRef(false)
  // Bumped on path change so an in-flight auto-open load cannot open/suppress after navigate.
  const autoOpenGenerationRef = useRef(0)
  const [isHovered, , openHovered, closeHovered] = useToggle()
  const [persistentOpen, setPersistentOpen] = useState(false)
  const [isAutoOpened, setIsAutoOpened] = useState(false)

  const canShowCartPreview = numberOfActiveItems > 0 && !isCartPage && allowCartPreview && !isMobile
  const showCartPreview = (isHovered || persistentOpen) && canShowCartPreview

  const debouncedOpenHovered = useMemo(() => debounce(openHovered, 200), [openHovered])
  const debouncedCloseHovered = useMemo(() => debounce(closeHovered, 500), [closeHovered])

  // Await the lazy chunk before flipping hover-open (same as auto-open). Do not
  // warm/preload on page load — that re-downloads the preview for every visitor
  // with cart items and defeats the code-split. Keep the 200ms open debounce so
  // sweeping across the icon does not flash the sheet when the chunk is warm.
  const handleMouseEnter = useCallback(() => {
    if (!canShowCartPreview) {
      return
    }
    hoverIntentRef.current = true
    debouncedCloseHovered.cancel()

    MultiCartDropdownPreview.load()
      .then(() => {
        if (!hoverIntentRef.current) {
          return
        }
        debouncedOpenHovered()
      })
      .catch((error: unknown) => {
        dispatch(ReportError(error, { component: 'MultiCartDropdownPreview', context: 'hover' }))
      })
  }, [canShowCartPreview, debouncedCloseHovered, debouncedOpenHovered, dispatch])

  const handleMouseLeave = useCallback(() => {
    hoverIntentRef.current = false
    debouncedOpenHovered.cancel()
    debouncedCloseHovered()
  }, [debouncedCloseHovered, debouncedOpenHovered])

  const handleClose = useCallback(() => {
    hoverIntentRef.current = false
    debouncedOpenHovered.cancel()
    setPersistentOpen(false)
    closeHovered()

    if (isAutoOpened) {
      setIsAutoOpened(false)
    }
  }, [closeHovered, debouncedOpenHovered, isAutoOpened])

  // Auto-open only after the preview chunk is ready, so we do not flash an empty
  // sheet. Suppress the cookie only once we successfully open.
  useEffect(() => {
    if (!canAutoOpen || isCartAutoPreviewDropdownSuppressed()) {
      return
    }

    const generation = autoOpenGenerationRef.current
    let cancelled = false

    MultiCartDropdownPreview.load()
      .then(() => {
        if (cancelled || generation !== autoOpenGenerationRef.current) return
        setPersistentOpen(true)
        setIsAutoOpened(true)
        suppressCartAutoDropdown()
      })
      .catch((error: unknown) => {
        if (cancelled || generation !== autoOpenGenerationRef.current) return
        dispatch(ReportError(error, { component: 'MultiCartDropdownPreview', context: 'autoOpen' }))
      })

    return () => {
      cancelled = true
    }
  }, [canAutoOpen, dispatch])

  // Auto close the cart preview when the user navigates to a different page.
  // Invalidate in-flight auto-open so a late load() cannot reopen or burn the cookie.
  useEffect(() => {
    autoOpenGenerationRef.current += 1
    setPersistentOpen(false)
    setIsAutoOpened(false)
  }, [currentPath])

  return (<div
    onMouseEnter={handleMouseEnter}
    onMouseLeave={!isAutoOpened ? handleMouseLeave : undefined}
  >
    <NavBarButton
      ref={buttonRef}
      className={props.className}
      data-testid="cart-button"
      to="/cart"
      onClick={handleClose}
      icon={<LineShoppingCartIcon />}
      badge={!!numberOfActiveItems && <Badge mode="critical" kind="string">
        {numberOfActiveItems}
      </Badge>}
    />
    {/* Mount only while open so the chunk is not requested on page load. */}
    {showCartPreview && <CSSBreakpoint min="desktop" showWhenEmpty>
      <MultiCartDropdownPreview
        open
        onClose={handleClose}
        buttonRef={buttonRef}
        hasDismissButton={isAutoOpened}
      />
    </CSSBreakpoint>}
  </div>)
}

export default memo(NavBarUserCartButton)
